import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { authAPI } from '../services/api';
import { Wallet, FileText } from 'lucide-react';
import { SampleReportModal } from '../components/SampleReportModal';

export function Login() {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('test@a.com');
  const [password, setPassword] = useState('test');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSampleReport, setShowSampleReport] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        // 로그인
        const response: any = await authAPI.login(email, password);
        if (response?.status === 'ok' && response?.data) {
          setAuth(response.data.user, response.data.token);
          navigate('/dashboard');
        }
      } else {
        // 회원가입 후 자동 로그인
        const registerResponse: any = await authAPI.register(email, password);
        if (registerResponse?.status === 'ok' && registerResponse?.data) {
          // 회원가입 성공 후 자동으로 로그인
          const loginResponse: any = await authAPI.login(email, password);
          if (loginResponse?.status === 'ok' && loginResponse?.data) {
            setAuth(loginResponse.data.user, loginResponse.data.token);
            navigate('/dashboard');
          } else {
            setError('회원가입은 완료되었지만 자동 로그인에 실패했습니다. 로그인해주세요.');
          }
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || (isLogin ? '로그인에 실패했습니다.' : '회원가입에 실패했습니다.'));
    } finally {
      setLoading(false);
    }
  };

  const handleWalletConnect = async () => {
    try {
      // MetaMask 또는 다른 지갑 확인
      if (typeof window.ethereum === 'undefined') {
        setError('MetaMask 또는 다른 Web3 지갑이 설치되어 있지 않습니다.');
        return;
      }

      setLoading(true);
      setError('');

      // 지갑 연결 요청
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts',
      });

      if (!accounts || accounts.length === 0) {
        setError('지갑 연결이 취소되었습니다.');
        setLoading(false);
        return;
      }

      const address = accounts[0];
      const chainId = await window.ethereum.request({
        method: 'eth_chainId',
      });

      // Nonce 요청
      const nonceResponse: any = await authAPI.getWalletNonce(address);
      if (nonceResponse?.status !== 'ok' || !nonceResponse?.data) {
        setError('Nonce 요청에 실패했습니다.');
        setLoading(false);
        return;
      }

      const { nonce, message } = nonceResponse.data;

      // 메시지 서명 요청
      const signature = await window.ethereum.request({
        method: 'personal_sign',
        params: [message, address],
      });

      // 지갑 연결 인증
      const response: any = await authAPI.walletConnect({
        address,
        chain: chainId,
        signature,
        message,
        nonce,
      });

      if (response?.status === 'ok' && response?.data) {
        setAuth(response.data.user, response.data.token);
        navigate('/dashboard');
      } else {
        setError('지갑 인증에 실패했습니다.');
      }
    } catch (err: any) {
      console.error('Wallet connect error:', err);
      setError(err.message || '지갑 연결에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-4xl font-bold text-primary-600">
              COINFUNDMENTAL
            </h1>
            <span className="px-3 py-1 text-sm font-semibold text-orange-600 bg-orange-100 rounded-full">
              BETA
            </span>
          </div>
          <p className="text-gray-600">
            펀더멘털 기반 가치평가 엔진
          </p>
        </div>

        <div className="card">
          <div className="flex mb-6">
            <button
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-2 text-center font-medium ${
                isLogin
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-gray-500'
              }`}
            >
              로그인
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-2 text-center font-medium ${
                !isLogin
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-gray-500'
              }`}
            >
              회원가입
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                이메일 (테스트 계정)
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                테스트 계정: test@a.com / test
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                비밀번호
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                required
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full"
            >
              {loading ? '처리 중...' : isLogin ? '로그인' : '회원가입'}
            </button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">또는</span>
              </div>
            </div>

            <button
              onClick={handleWalletConnect}
              className="mt-4 btn btn-secondary w-full flex items-center justify-center"
            >
              <Wallet className="w-5 h-5 mr-2" />
              지갑 연결
            </button>
          </div>

          {/* 샘플 리포트 링크 */}
          <div className="mt-6 text-center">
            <button
              onClick={() => setShowSampleReport(true)}
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center justify-center mx-auto"
            >
              <FileText className="w-4 h-4 mr-2" />
              샘플 리포트 보기
            </button>
          </div>

          {/* Footer 링크 */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex justify-center gap-4 text-xs text-gray-500">
              <a href="/privacy" className="hover:text-primary-600">
                개인정보처리방침
              </a>
              <span className="text-gray-300">|</span>
              <a href="/service" className="hover:text-primary-600">
                서비스 이용약관
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* 샘플 리포트 모달 */}
      <SampleReportModal
        isOpen={showSampleReport}
        onClose={() => setShowSampleReport(false)}
      />
    </div>
  );
}

